import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { User, Mail, Phone, Lock, Save } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProfileSettings = ({ user, onUpdateUser }) => {
  const [loading, setLoading] = useState(false);
  const [profile, setProfile] = useState(null);
  
  // Profile data
  const [profileData, setProfileData] = useState({
    name: '',
    phone: '',
    city: '',
    zip_code: ''
  });
  
  // Password change
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  
  // Email change
  const [emailData, setEmailData] = useState({
    new_email: '',
    password: ''
  });
  

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      const token = localStorage.getItem('token');
      let endpoint = '';
      
      if (user.role === 'customer') {
        endpoint = `${API}/customer/profile`;
      } else if (user.role === 'provider') {
        endpoint = `${API}/provider/profile`;
      }
      
      const response = await axios.get(endpoint, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setProfile(response.data);
      
      if (user.role === 'customer') {
        setProfileData({
          name: response.data.name || '',
          phone: response.data.phone || '',
          city: response.data.city || '',
          zip_code: response.data.zip_code || ''
        });
        
      }
    } catch (error) {
      console.error('Error loading profile:', error);
      toast.error('Fehler beim Laden des Profils');
    }
  };

  const handleUpdateProfile = async () => {
    if (user.role !== 'customer') return;

    if (profileData.zip_code && profileData.zip_code.length > 10) {
      toast.error('PLZ darf maximal 10 Zeichen lang sein');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/customer/profile`, profileData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Profil aktualisiert');
    } catch (error) {
      console.error('Error updating profile:', error);
      const message = error.response?.data?.detail || 'Fehler beim Aktualisieren des Profils';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('Passwörter stimmen nicht überein');
      return;
    }
    
    if (passwordData.new_password.length < 8) {
      toast.error('Das Passwort muss mindestens 8 Zeichen lang sein');
      return;
    }

    if (!/[a-zA-Z]/.test(passwordData.new_password)) {
      toast.error('Das Passwort muss mindestens einen Buchstaben enthalten');
      return;
    }

    if (!/[0-9]/.test(passwordData.new_password)) {
      toast.error('Das Passwort muss mindestens eine Zahl enthalten');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/settings/change-password`, {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Passwort erfolgreich geändert');
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      console.error('Error changing password:', error);
      const message = error.response?.data?.detail || 'Fehler beim Ändern des Passworts';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleChangeEmail = async (e) => {
    e.preventDefault();
    
    if (!emailData.new_email || !emailData.password) {
      toast.error('Bitte alle Felder ausfüllen');
      return;
    }
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/settings/change-email`, {
        new_email: emailData.new_email,
        password: emailData.password
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('E-Mail-Adresse erfolgreich geändert');
      if (onUpdateUser) {
        onUpdateUser({ email: emailData.new_email, email_verified: false });
      }
      setEmailData({ new_email: '', password: '' });
    } catch (error) {
      console.error('Error changing email:', error);
      const message = error.response?.data?.detail || 'Fehler beim Ändern der E-Mail';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };


  return (
    <div className="space-y-6">
      {/* Profile Data (Customer only) */}
      {user.role === 'customer' && (
        <div className="bg-white rounded-2xl shadow-sm p-6">
          <div className="flex items-center gap-3 mb-6">
            <User className="w-6 h-6 text-[#2B2142]" />
            <h3 className="text-xl font-bold text-gray-900">Profildaten</h3>
          </div>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Name</label>
              <input
                type="text"
                value={profileData.name}
                onChange={(e) => setProfileData({...profileData, name: e.target.value})}
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-[#2B2142] focus:outline-none"
                placeholder="Max Mustermann"
              />
            </div>
            
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Telefon</label>
              <input
                type="tel"
                inputMode="tel"
                maxLength={20}
                pattern="[+\d\s\-()]*"
                value={profileData.phone}
                onChange={(e) => setProfileData({...profileData, phone: e.target.value.replace(/[^\d+\s\-()]/g, '')})}
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-[#2B2142] focus:outline-none"
                placeholder="+49 123 456789"
              />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Stadt</label>
                <input
                  type="text"
                  value={profileData.city}
                  onChange={(e) => setProfileData({...profileData, city: e.target.value})}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-[#2B2142] focus:outline-none"
                  placeholder="Berlin"
                />
              </div>
              
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">PLZ</label>
                <input
                  type="text"
                  value={profileData.zip_code}
                  onChange={(e) => setProfileData({...profileData, zip_code: e.target.value})}
                  maxLength={10}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-[#2B2142] focus:outline-none"
                  placeholder="10115"
                />
              </div>
            </div>
            
            <button
              onClick={handleUpdateProfile}
              disabled={loading}
              className="w-full md:w-auto px-6 py-3 bg-[#2B2142] hover:bg-[#1E1730] text-white font-semibold rounded-xl transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              <Save className="w-5 h-5" />
              Speichern
            </button>
          </div>
        </div>
      )}

      {/* Password Change */}
      <div className="bg-white rounded-2xl shadow-sm p-6">
        <div className="flex items-center gap-3 mb-6">
          <Lock className="w-6 h-6 text-[#2B2142]" />
          <h3 className="text-xl font-bold text-gray-900">Passwort ändern</h3>
        </div>
        
        <form onSubmit={handleChangePassword} className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Aktuelles Passwort</label>
            <input
              type="password"
              value={passwordData.current_password}
              onChange={(e) => setPasswordData({...passwordData, current_password: e.target.value})}
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-[#2B2142] focus:outline-none"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Neues Passwort</label>
            <input
              type="password"
              value={passwordData.new_password}
              onChange={(e) => setPasswordData({...passwordData, new_password: e.target.value})}
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-[#2B2142] focus:outline-none"
              required
              minLength={8}
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Neues Passwort bestätigen</label>
            <input
              type="password"
              value={passwordData.confirm_password}
              onChange={(e) => setPasswordData({...passwordData, confirm_password: e.target.value})}
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-[#2B2142] focus:outline-none"
              required
              minLength={8}
            />
          </div>
          
          <button
            type="submit"
            disabled={loading}
            className="w-full md:w-auto px-6 py-3 bg-[#2B2142] hover:bg-[#1E1730] text-white font-semibold rounded-xl transition-colors disabled:opacity-50"
          >
            Passwort ändern
          </button>
        </form>
      </div>

      {/* Email Change */}
      <div className="bg-white rounded-2xl shadow-sm p-6">
        <div className="flex items-center gap-3 mb-6">
          <Mail className="w-6 h-6 text-[#2B2142]" />
          <h3 className="text-xl font-bold text-gray-900">E-Mail-Adresse ändern</h3>
        </div>
        
        <div className="mb-4 p-4 bg-[#FFF0D1]/30 border border-[#2B2142]/20 rounded-xl">
          <p className="text-sm text-[#2B2142]">
            <strong>Aktuelle E-Mail:</strong> {user.email}
          </p>
        </div>
        
        <form onSubmit={handleChangeEmail} className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Neue E-Mail-Adresse</label>
            <input
              type="email"
              value={emailData.new_email}
              onChange={(e) => setEmailData({...emailData, new_email: e.target.value})}
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-[#2B2142] focus:outline-none"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Passwort zur Bestätigung</label>
            <input
              type="password"
              value={emailData.password}
              onChange={(e) => setEmailData({...emailData, password: e.target.value})}
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-[#2B2142] focus:outline-none"
              required
            />
          </div>
          
          <button
            type="submit"
            disabled={loading}
            className="w-full md:w-auto px-6 py-3 bg-[#2B2142] hover:bg-[#1E1730] text-white font-semibold rounded-xl transition-colors disabled:opacity-50"
          >
            E-Mail ändern
          </button>
        </form>
      </div>

    </div>
  );
};

export default ProfileSettings;
